import React, { useState, useEffect, useCallback } from 'react';
import { useUserRoles } from '../../contexts/UserRolesContext';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useCache } from '../../contexts/CacheContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { createCachedStatisticsService } from '../../services/cachedStatisticsService';
import { EnhancedDashboardStats, CaseStatusData } from '../../types/dashboard';
import {
  RevenueTrendChart,
  CaseStatusDistribution,
  ClientGrowthChart,
  EnhancedDashboardCard
} from './charts';
import { format } from 'date-fns';
import { RefreshCcw, BarChart3 } from 'lucide-react';
import { SectionHeader } from '../../components/ui';
import FeatureAccessPlaceholder from '../../components/subscription/FeatureAccessPlaceholder';

// Get enhanced dashboard stats from cached data
const getEnhancedDashboardStats = (cache: any): EnhancedDashboardStats => {
  const statisticsService = createCachedStatisticsService(cache);
  return statisticsService.getEnhancedDashboardStats();
};

const Statistics: React.FC = () => {
  
  const { roles, loading: rolesLoading } = useUserRoles();
  const { strings, language, isRTL } = useLocalization();
  const { cache, isLoaded: cacheLoaded, isLoading: cacheLoading } = useCache();
  const { isFeatureAvailable } = useSubscription();
  const [stats, setStats] = useState<EnhancedDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  // Check if user has access to advanced statistics
  const hasAdvancedStats = isFeatureAvailable('advancedStats');

  const fetchStats = useCallback((isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      if (rolesLoading || !cacheLoaded) {
        return;
      }

      console.log('🔄 Generating enhanced dashboard statistics from cached data');
      console.log('📊 Cache data summary:', {
        offices: cache?.offices?.length || 0,
        clients: cache?.clients?.length || 0,
        events: cache?.events?.length || 0,
        legalFiles: cache?.legalFiles?.length || 0,
        roles: cache?.roles?.length || 0
      });
      console.log('📊 Full cache object:', cache);

      const dashboardStats = getEnhancedDashboardStats(cache);
      setStats(dashboardStats);
      setLastUpdated(new Date());

      console.log('✅ Enhanced dashboard statistics generated from cached data successfully');
    } catch (err) {
      console.error('❌ Error generating dashboard statistics from cached data:', err);
      setError(strings.dashboard.loadError);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [roles, rolesLoading, cacheLoaded, cache, strings.dashboard.loadError]);

  const handleRefresh = () => {
    fetchStats(true);
  };

  useEffect(() => {
    console.log('🔄 EnhancedDashboard useEffect triggered:', {
      rolesLoading,
      cacheLoading,
      cacheLoaded,
      rolesLength: roles?.length || 0,
      cache: cache ? 'present' : 'missing'
    });
    
    if (!rolesLoading && !cacheLoading && cacheLoaded && roles.length > 0) {
      console.log('✅ All conditions met, calling fetchStats');
      fetchStats();
    } else {
      console.log('❌ Conditions not met for fetchStats:', {
        rolesLoading,
        cacheLoading,
        cacheLoaded,
        rolesLength: roles?.length || 0
      });
    }
  }, [roles, rolesLoading, cacheLoading, cacheLoaded, fetchStats]);

  // Show placeholder if user doesn't have access
  if (!hasAdvancedStats) {
    return (
      <FeatureAccessPlaceholder
        featureName="Advanced Statistics"
        featureNameAr="الإحصائيات المتقدمة"
        description="Get detailed insights into your practice with advanced analytics, revenue trends, and performance metrics."
        descriptionAr="احصل على رؤى مفصلة حول ممارستك مع التحليلات المتقدمة واتجاهات الإيرادات ومقاييس الأداء."
        icon={<BarChart3 className="w-12 h-12 text-white" />}
      />
    );
  }

  // Loading state
  if (loading || rolesLoading || cacheLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-primary/10 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
          <h2 className="m3-headline-small text-slate-800 dark:text-white mb-2">{strings.dashboard.loading}</h2>
          <p className="m3-body-large text-slate-600 dark:text-slate-300">
            {rolesLoading ? strings.dashboard.loadingRoles : 
             cacheLoading ? 'Loading cached data...' : 
             strings.dashboard.loadingStats}
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-primary/10 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 dark:text-red-400 text-2xl">⚠️</span>
          </div>
          <h2 className="m3-headline-small text-slate-800 dark:text-white mb-2">{strings.dashboard.errorTitle}</h2>
          <p className="m3-body-large text-slate-600 dark:text-slate-300 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-3 bg-primary text-white rounded-2xl hover:bg-primary-dark transition-colors duration-300 m3-elevation-2 hover:m3-elevation-3"
          >
            {strings.common.refresh}
          </button>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-primary/10 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6 flex items-center justify-center">
        <h2 className="m3-headline-small text-slate-800 dark:text-white">{strings.dashboard.noData}</h2>
      </div>
    );
  }


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-primary/5 to-accent/5 dark:from-slate-900 dark:via-primary/10 dark:to-accent/10 p-8 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-8 dark:opacity-12">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_50%_50%,rgba(26,43,76,0.15),transparent_60%)]"></div>
        <div className="absolute top-1/4 right-0 w-96 h-96 bg-[radial-gradient(circle_at_50%_50%,rgba(201,161,75,0.12),transparent_60%)] rounded-full"></div>
        <div className="absolute bottom-0 left-1/4 w-64 h-64 bg-[radial-gradient(circle_at_50%_50%,rgba(46,125,50,0.12),transparent_60%)] rounded-full"></div>
      </div>

      {/* Header */}
      <div className="relative mb-16 animate-fade-in-up">
        <SectionHeader
          title={strings.dashboard.title}
          description={strings.dashboard.welcome}
          language={language}
          isRTL={isRTL}
          refreshButton={{
            onClick: handleRefresh,
            disabled: refreshing,
            loading: refreshing
          }}
        >
          {/* Last Updated Info */}
          <div className={`flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`}>
            <div className={`${isRTL ? 'text-left' : 'text-right'}`}>
              <div className={`flex items-center ${isRTL ? 'justify-start' : 'justify-end'} space-x-2 mb-1`}>
                <p className="m3-label-medium text-slate-500 dark:text-slate-400">{strings.dashboard.lastUpdated}</p>
                {refreshing && (
                  <div className="flex items-center space-x-1">
                    <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
                    <span className="m3-label-small text-blue-600 dark:text-blue-400 font-medium">{strings.common.loading}</span>
                  </div>
                )}
              </div>
              <p className="m3-body-medium text-slate-700 dark:text-slate-300">
                {lastUpdated ? format(lastUpdated, 'p, MMM d, yyyy', { locale: language === 'ar' ? undefined : undefined }) : 'N/A'}
              </p>
            </div>
          </div>
        </SectionHeader>
      </div>

      {/* Refresh Status Banner */}
      {refreshing && (
        <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl m3-elevation-1 animate-fade-in-up">
          <div className="flex items-center justify-center space-x-3">
            <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            <span className="m3-label-large text-blue-700 dark:text-blue-300 font-semibold">
              {strings.dashboard.updating}
            </span>
          </div>
        </div>
      )}

      {/* Data Notice */}
      <div className="mb-8 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 dark:bg-blue-800 rounded-full flex items-center justify-center">
              <span className="text-blue-600 dark:text-blue-400 text-sm">📊</span>
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              {strings.dashboard.dataNotice}
            </h3>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              {strings.dashboard.dataNoticeDescription}
            </p>
            <div className="mt-2 text-xs text-blue-600 dark:text-blue-400">
              <strong>Data Source:</strong> Cached from your offices, clients, events, and legal files
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid - Only show real data */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 mb-16 transition-all duration-500 ${refreshing ? 'opacity-60 scale-98' : 'opacity-100 scale-100'}`}>
        <EnhancedDashboardCard
          title={strings.dashboard.myClients}
          value={stats.myClients}
          icon="👥"
          color="bg-primary"
          bgColor="bg-primary"
          trend={stats.myClientsTrend}
          description={strings.dashboard.live}
        />
        <EnhancedDashboardCard
          title={strings.dashboard.myOffices}
          value={stats.myOffices}
          icon="🏢"
          color="bg-accent"
          bgColor="bg-accent"
          trend={stats.myOfficesTrend}
          description={strings.dashboard.active}
        />
        <EnhancedDashboardCard
          title={strings.dashboard.upcomingEvents}
          value={stats.upcomingEvents}
          icon="📅"
          color="bg-primary-light"
          bgColor="bg-primary-light"
          trend={stats.upcomingEventsTrend}
          description={strings.dashboard.next30Days}
        />
        <EnhancedDashboardCard
          title={strings.dashboard.legalFiles}
          value={stats.legalFiles}
          icon="📄"
          color="bg-accent-light"
          bgColor="bg-accent-light"
          trend={stats.legalFilesTrend}
          description={strings.dashboard.documents}
        />
        <EnhancedDashboardCard
          title={strings.dashboard.recentActivity}
          value={stats.recentActivity}
          icon="⚡"
          color="bg-primary"
          bgColor="bg-primary"
          trend={stats.recentActivityTrend}
          description={strings.dashboard.last7Days}
        />
      </div>

      {/* Financial Overview - Only show if we have revenue data */}
      {stats.totalRevenue > 0 && (
        <div className="mb-16 animate-fade-in-up">
          <h2 className="m3-headline-small text-slate-800 dark:text-white mb-6">{strings.dashboard.financialOverview}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <EnhancedDashboardCard
              title={strings.dashboard.totalRevenue}
              value={stats.totalRevenue}
              icon="💰"
              color="bg-accent"
              bgColor="bg-accent"
              unit=" EGP"
            />
            <EnhancedDashboardCard
              title={strings.dashboard.totalExpenses}
              value={stats.totalExpenses}
              icon="💸"
              color="bg-red"
              bgColor="bg-red"
              unit=" EGP"
            />
            <EnhancedDashboardCard
              title={strings.dashboard.netProfit}
              value={stats.netProfit}
              icon="📈"
              color="bg-green"
              bgColor="bg-green"
              unit=" EGP"
            />
          </div>
          {stats.revenueTrend.length > 0 && (
            <RevenueTrendChart
              data={stats.revenueTrend}
              height={400}
              showArea={true}
            />
          )}
        </div>
      )}

      {/* Client Analytics - Only show if we have client data */}
      {stats.clientAcquisitionFunnel.length > 0 && (
        <div className="mb-16 animate-fade-in-up">
          <h2 className="m3-headline-small text-slate-800 dark:text-white mb-6">{strings.dashboard.clientAnalytics}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ClientGrowthChart
              data={stats.clientAcquisitionFunnel}
              type="acquisition"
              height={300}
            />
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 m3-elevation-1 shadow-lg">
              <h3 className="m3-title-large text-slate-800 dark:text-white mb-4">{strings.dashboard.clientGrowthRate}</h3>
              <p className="m3-display-small text-slate-800 dark:text-white font-bold">
                {stats.clientGrowthRate.toFixed(1)}%
              </p>
              <p className="m3-body-large text-slate-600 dark:text-slate-300">
                {strings.dashboard.newClientsLast30Days}: {stats.newClientsLast30Days}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Case Performance Analytics - Only show if we have case data */}
      {stats.caseStatusDistribution.length > 0 && (
        <div className="mb-16 animate-fade-in-up">
          <h2 className="m3-headline-small text-slate-800 dark:text-white mb-6">{strings.dashboard.casePerformance}</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <CaseStatusDistribution 
              data={stats.caseStatusDistribution} 
              height={300}
              showLegend={true}
            />
            <div className="bg-white dark:bg-slate-800 rounded-2xl p-6 m3-elevation-1 shadow-lg">
              <h3 className="m3-title-large text-slate-800 dark:text-white mb-4">{strings.dashboard.totalCases}</h3>
              <p className="m3-display-small text-slate-800 dark:text-white font-bold">
                {stats.legalFiles}
              </p>
              <p className="m3-body-large text-slate-600 dark:text-slate-300">
                {strings.dashboard.activeCases}: {stats.caseStatusDistribution.find((s: CaseStatusData) => s.status === 'active')?.count || 0}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Data Availability Notice */}
      <div className="mb-16 animate-fade-in-up">
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6 m3-elevation-1">
          <h3 className="m3-title-large text-blue-800 dark:text-blue-200 mb-2">{strings.dashboard.dataNotice}</h3>
          <p className="m3-body-large text-blue-700 dark:text-blue-300">
            {strings.dashboard.dataNoticeDescription}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Statistics;